import { createSlice } from '@reduxjs/toolkit';

import {
  fetchTeamMembersThunk,
  createTeamMemberThunk,
  reactivateTeamMemberThunk,
  updateTeamMemberThunk,
  updateTeamMemberStatusThunk,
  deleteTeamMemberThunk,
} from './teamThunk';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: string;
  post: string;
  department: string;
  status: string;
  joined?: string;

  [key: string]: any;
}

interface TeamState {
  members: TeamMember[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  hasLoaded: boolean;
}

const initialState: TeamState = {
  members: [],
  loading: false,
  saving: false,
  error: null,
  hasLoaded: false,
};

/*
API response me actual team member ko nikalne ke liye.
Ye different response structures handle karega.
*/
const getMembersFromResponse = (
  payload: any
): TeamMember[] => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.data?.members)) {
    return payload.data.members;
  }

  if (Array.isArray(payload?.data?.teamMembers)) {
    return payload.data.teamMembers;
  }

  if (Array.isArray(payload?.members)) {
    return payload.members;
  }

  return [];
};

const getMemberFromResponse = (
  payload: any
): TeamMember | null => {
  return (
    payload?.data?.teamMember ||
    payload?.data?.member ||
    payload?.teamMember ||
    payload?.member ||
    payload?.data ||
    null
  );
};

const teamSlice = createSlice({
  name: 'team',

  initialState,

  reducers: {
    clearTeamError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder

      // =========================
      // GET TEAM MEMBERS
      // =========================

      .addCase(
        fetchTeamMembersThunk.pending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )

      .addCase(fetchTeamMembersThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.hasLoaded = true;
        state.members = getMembersFromResponse(action.payload);
      })

      .addCase(fetchTeamMembersThunk.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || 'Failed to fetch team members';
      })

      // =========================
      // CREATE TEAM MEMBER
      // =========================

      .addCase(
        createTeamMemberThunk.pending,
        (state) => {
          state.saving = true;
          state.error = null;
        }
      )

      .addCase(
        createTeamMemberThunk.fulfilled,
        (state, action) => {
          state.saving = false;

          const newMember =
            getMemberFromResponse(
              action.payload
            );

          if (newMember?.id) {
            state.members.unshift(
              newMember
            );
          }
        }
      )

      .addCase(
        createTeamMemberThunk.rejected,
        (state, action) => {
          state.saving = false;

          /*
          createTeamMemberThunk can now return
          a structured error object because the
          Reactivation flow needs errorCode/data.

          The structured error is still available
          through .unwrap() in HRMyTeamPage.

          Redux state keeps only the readable
          message so the existing UI remains unchanged.
          */
          state.error =
            action.payload?.message ||
            'Failed to create team member';
        }
      )

      // =========================
      // REACTIVATE TEAM MEMBER
      // =========================

      .addCase(
        reactivateTeamMemberThunk.pending,
        (state) => {
          state.saving = true;
          state.error = null;
        }
      )

      .addCase(
        reactivateTeamMemberThunk.fulfilled,
        (state, action) => {
          state.saving = false;

          const reactivatedMember =
            getMemberFromResponse(
              action.payload
            );

          if (!reactivatedMember?.id) {
            return;
          }

          const index =
            state.members.findIndex(
              (member) =>
                member.id ===
                reactivatedMember.id
            );

          if (index !== -1) {
            state.members[index] = {
              ...state.members[index],
              ...reactivatedMember,
            };
          } else {
            state.members.unshift(
              reactivatedMember
            );
          }
        }
      )

      .addCase(
        reactivateTeamMemberThunk.rejected,
        (state, action) => {
          state.saving = false;

          state.error =
            action.payload?.message ||
            'Failed to reactivate team member';
        }
      )

      // =========================
      // UPDATE TEAM MEMBER
      // =========================

      .addCase(
        updateTeamMemberThunk.pending,
        (state) => {
          state.saving = true;
          state.error = null;
        }
      )

      .addCase(
        updateTeamMemberThunk.fulfilled,
        (state, action) => {
          state.saving = false;

          const updatedMember =
            getMemberFromResponse(
              action.payload
            );

          if (!updatedMember?.id) {
            return;
          }

          const index =
            state.members.findIndex(
              (member) =>
                member.id ===
                updatedMember.id
            );

          if (index !== -1) {
            state.members[index] = {
              ...state.members[index],
              ...updatedMember,
            };
          }
        }
      )

      .addCase(
        updateTeamMemberThunk.rejected,
        (state, action) => {
          state.saving = false;

          state.error =
            action.payload ||
            'Failed to update team member';
        }
      )

      // =========================
      // UPDATE STATUS
      // =========================

      .addCase(
        updateTeamMemberStatusThunk.pending,
        (state) => {
          state.error = null;
        }
      )

      .addCase(
        updateTeamMemberStatusThunk.fulfilled,
        (state, action) => {
          const updatedMember =
            getMemberFromResponse(
              action.payload
            );

          if (!updatedMember?.id) {
            return;
          }

          const index =
            state.members.findIndex(
              (member) =>
                member.id ===
                updatedMember.id
            );

          if (index !== -1) {
            state.members[index] = {
              ...state.members[index],
              ...updatedMember,
            };
          }
        }
      )

      .addCase(
        updateTeamMemberStatusThunk.rejected,
        (state, action) => {
          state.error =
            action.payload ||
            'Failed to update member status';
        }
      )

      // =========================
      // DELETE TEAM MEMBER
      // =========================

      .addCase(
        deleteTeamMemberThunk.fulfilled,
        (state, action) => {
          state.members =
            state.members.filter(
              (member) =>
                member.id !== action.payload
            );
        }
      )

      .addCase(
        deleteTeamMemberThunk.rejected,
        (state, action) => {
          state.error =
            action.payload ||
            'Failed to delete team member';
        }
      );
  },
});

export const {
  clearTeamError,
} = teamSlice.actions;

export default teamSlice.reducer;